import React, { useCallback, useEffect, useState } from 'react';
import { MCButton, MCError, MCText, SelectField, Spinner, View } from '../basic';
import { api, lang, rangeToSelectList, remCss, scssVariables as variables } from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import moment from 'moment';
import { rangeTimeOfGroup, SlotTag, Toggle, updateGroup } from './utils';
import mainModel, { models, STEPS } from './model';
import AlertManager from 'src/components/alert/AlertManager';
import errorDescriptions from 'src/constants/errors';

const model = models[STEPS.GROUP_SLOTS];

export const GroupSlots = () => {
  model.use();
  const doctorId = mainModel.state.doctorId;
  const { group, startDate, endDate } = model.state;

  const [invalid, setInvalid] = useState({});
  const [list, setList] = useState([]);
  const [initLoading, setInitLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadList = useCallback(
    (startDate, endDate, refresh) => {
      if (initLoading) return;
      refresh && setInitLoading(true);
      api('schedule/group', {
        from: startDate,
        to: endDate,
        id: doctorId,
        group,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }).then(({ error, data }) => {
        if (!error) {
          setList(data);
        } else {
          setInvalid({ error: errorDescriptions(error) });
        }
        setInitLoading(false);
      });
    },
    [doctorId, group, initLoading]
  );

  const refresh = useCallback(
    (refresh = false) => {
      loadList(startDate, endDate, refresh);
    },
    [startDate, endDate, loadList]
  );

  const toggle = useCallback(
    (...args) => {
      const func = (item, parent, enabled, cb, extra) => {
        if (loading) return;
        setLoading(true);
        const params = {
          day: item.id,
          id: doctorId,
          group: item.group,
          enabled,
          extra,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          resolvedList: []
        };
        const url = 'schedule/enableGroup';
        api(url, params).then(({ error, data }) => {
          if (!error) {
            if (data.bookedList) {
              AlertManager.popup(AlertManager.TYPES.INFO, {
                width: remCss(600),
                close: true,
                title: LANG_CONST.notice.booking_slot_when_disabled,
                actionButton: {
                  label: LANG_CONST.buttons.resolve_bookings,
                  action: (actions) => {
                    actions.hide();

                    mainModel.setNextStep(STEPS.BOOKED_LIST, {
                      list: data.bookedList,
                      params: rangeTimeOfGroup(item, parent),
                      unsavedSlots: [],
                      onDone: (resolvedList, cb) => {
                        api(url, { ...params, resolvedList }).then(({ error, data }) => {
                          cb(error, data);
                          if (!error) {
                            mainModel.backStep();
                            refresh();
                          }
                        });
                      }
                    });
                  }
                },
                cancelButton: {
                  label: LANG_CONST.buttons.turn_off_extra_bookings,
                  action: (actions) => {
                    func(
                      item,
                      parent,
                      enabled,
                      (...arg) => {
                        cb && cb(...arg);
                        actions.hide();
                      },
                      true
                    );
                  }
                }
              });
            } else if (!extra) setList((o) => updateGroup(o, item.group, item.id, enabled));
          } else {
            setInvalid({ error: errorDescriptions(error) });
          }
          setLoading(false);
          cb && cb(error, data);
        });
      };
      func(...args);
    },
    [loading, doctorId, refresh]
  );

  useEffect(() => {
    const func = () => {
      refresh(true);
    };

    func();
  }, [startDate, endDate]);

  const onRemake = useCallback(() => {
    mainModel.setNextStep(STEPS.GENERATE_SCHEDULE, { startDate, endDate });
  }, [startDate, endDate]);

  let title =
    group !== 'year'
      ? moment(startDate).format('MMMM DD - ') + moment(endDate).format('MMMM DD')
      : lang.t(LANG_CONST.titles.working_schedule);

  return (
    <>
      <div className='mc-flex-20 mc-b'>
        <div className='mc-popup-title mc-grow'>{title}</div>
        {mainModel.canModify() && (
          <MCButton label={LANG_CONST.buttons.remake} light medium radius onClick={onRemake} />
        )}
        {group === 'year' && (
          <SelectField
            template={6}
            isClearable={false}
            options={rangeToSelectList(moment().year(), moment().year() + 50)}
            value={moment(startDate).year()}
            onChange={(n, value) => {
              model.setState({
                startDate: Math.max(moment().year(value).startOf('year').valueOf(), Date.now()),
                endDate: moment().year(value).endOf('year').valueOf()
              });
            }}
          />
        )}
      </div>

      {invalid.error && (
        <div style={{ paddingBottom: 0 }}>
          <MCError error={invalid.error} />
        </div>
      )}
      {initLoading ? (
        <View>
          <Spinner />
        </View>
      ) : (
        <div
          className='mc-modal-body mc-flex-col'
          style={{ flex: 1, gap: remCss(10), padding: remCss(20) }}
        >
          <List
            toggle={toggle}
            list={list}
            goToSlots={(item) => {
              mainModel.setNextStep(STEPS.SLOTS_OF_DAYS, {
                selectedDay: item.startDate,
                onBack: () => loadList(startDate, endDate)
              });
            }}
            onRemake={(item, parent) => {
              const onDone = () => {
                refresh();
                mainModel.backStep();
              };
              mainModel.setNextStep(STEPS.GENERATE_SCHEDULE, {
                ...rangeTimeOfGroup(item, parent),
                onDone
              });
            }}
            onSlotTag={(item, parent, tab) => {
              const onBack = () => {
                refresh();
              };

              const { startDate, endDate } = rangeTimeOfGroup(item, parent);

              mainModel.setNextStep(STEPS.SLOTS_OF_RANGE, {
                item,
                parent,
                tab,
                startDate: moment(startDate).startOf('day').valueOf(),
                endDate,
                title: labelDate({ item, parent }),
                onBack
              });
            }}
          />
        </div>
      )}
    </>
  );
};

const labelDate = ({ item, parent }) => {
  switch (item.group) {
    case 'month':
      return moment(item.id).format('MMMM');
    case 'week':
      return (
        moment(Math.max(Date.now(), item.id, parent.id)).format('MMM DD - ') +
        moment(
          Math.min(
            moment(item.id).endOf('isoWeek').valueOf(),
            moment(parent.id).endOf('month').valueOf()
          )
        ).format('MMM DD')
      );
    default:
      return moment(item.id).format('dddd, MMM D');
  }
};

const LabelDate = ({ item, parent }) => {
  let text = labelDate({ item, parent }),
    size,
    w;
  switch (item.group) {
    case 'month':
      size = 20;
      w = 500;
      break;
    case 'week':
      size = 18;
      w = 500;
      break;
    default:
      w = 400;
      size = 18;
      break;
  }
  return (
    <MCText size={size} weight={w} style={{ flex: 1, maxWidth: remCss(150) }}>
      {text}
    </MCText>
  );
};

const List = ({ list, onRemake, toggle, parent, goToSlots, onSlotTag }) => {
  const [openId, setOpenId] = React.useState(null);
  return (
    <View style={{ gap: remCss(15) }}>
      {list.map((item) => {
        const { hasSlot, id, items, group } = item;
        return (
          <div
            className={`mc-flex-col mc-radius-5 mc-p10 ${group === 'week' ? 'mc-b2' : ''}`}
            key={id}
          >
            <div className={'mc-grow mc-flex-20'}>
              <Toggle
                slot={item}
                isAsync={true}
                disabled={!mainModel.canModify()}
                toggleSlot={(s, v, cb) => {
                  if (hasSlot) toggle(s, parent, v, cb);
                  else
                    AlertManager.popup(AlertManager.TYPES.WARN, {
                      close: false,
                      maxWidth: remCss(450),
                      title: lang.t(LANG_CONST.notice.no_schedule, {
                        time: labelDate({ item, parent })
                      }),
                      cancelButton: {
                        label: LANG_CONST.buttons.no,
                        action: (actions) => actions.hide()
                      },
                      actionButton: {
                        label: LANG_CONST.buttons.yes,
                        action: ({ hide }) => {
                          hide();
                          onRemake(item, parent);
                        }
                      }
                    });
                }}
                label={
                  <View style={{ flexDirection: 'row', flex: 1, alignItems: 'center' }}>
                    <LabelDate item={item} parent={parent} />
                    <SlotTag item={item} parent={parent} onSlotTag={onSlotTag} />
                  </View>
                }
              />
              {mainModel.canModify() && (
                <MCButton
                  label={lang.t(LANG_CONST.buttons.remake)}
                  light
                  radius
                  onClick={() => onRemake(item, parent)}
                />
              )}
              {items ? (
                <MCButton
                  onClick={() => setOpenId((o) => (o === id ? null : id))}
                  tiny
                  primary
                  light
                  radius
                >
                  <span
                    className={'mc-rotate ' + (openId === id ? 'mc-rotate-180' : '')}
                    style={{ height: remCss(44) }}
                  >
                    <SVGIcon
                      iconname={BASE_ICONS.DOWN}
                      width={44}
                      padding={7}
                      fill={variables['colors-mc-primary']}
                    />
                  </span>
                </MCButton>
              ) : (
                <MCButton
                  onClick={() => goToSlots(item)}
                  tiny
                  primary
                  light
                  radius
                  width={44}
                  iconname={BASE_ICONS.RIGHT}
                />
              )}
            </div>
            {items && openId === id && (
              <List
                list={items}
                onRemake={onRemake}
                toggle={toggle}
                parent={item}
                goToSlots={goToSlots}
                onSlotTag={onSlotTag}
              />
            )}
          </div>
        );
      })}
    </View>
  );
};
